import { calculatePercentChange, safeDivide } from 'src/utils/app';
import { getNextYearWeekId, weekIdToTimestamp } from 'src/utils/dateUtils';
import { ComparisonPeriod, useAppSelector, useComparisonPeriod, useMainTimePeriod } from 'src/utils/Hooks';

const getFilteredRangeData = ({
  data,
  startWeek,
  endWeek
}: {
  data: [number, number][];
  startWeek: number;
  endWeek: number;
}) => {
  if (startWeek === endWeek) {
    return data.filter(([weekId]) => {
      const weekIdNum = Number(weekId);
      return weekIdNum === startWeek;
    });
  }

  return data.filter(([weekId]) => {
    const weekIdNum = Number(weekId);
    return weekIdNum >= startWeek && weekIdNum <= endWeek;
  });
};

const getAverage = (data: [number, number][]) => {
  return safeDivide(
    data.reduce((acc, [_, value]) => acc + value, 0),
    data.length
  );
};

const sumFilteredRangeData = (data: [number, number][], startWeek: number, endWeek: number) =>
  getFilteredRangeData({ data, startWeek, endWeek }).reduce((acc, [_, value]) => acc + value, 0);

const calculateDerivedMetricAverage = ({
  numeratorData,
  denominatorData,
  startWeek,
  endWeek
}: {
  numeratorData: [number, number][];
  denominatorData: [number, number][];
  startWeek: number;
  endWeek: number;
}) => {
  const numeratorSum = sumFilteredRangeData(numeratorData, startWeek, endWeek);
  const denominatorSum = sumFilteredRangeData(denominatorData, startWeek, endWeek);
  return safeDivide(numeratorSum, denominatorSum);
};

/**
 *
 * Instead of fetching data from in the hook, we are passing in the data from the server proxy, (format will be [weekId, value]) to get requiredData for the chart
 */
export const useChartMetricByWeekId = ({
  primaryRawData,
  secondaryRawData,
  primaryDerivedMetricRawData,
  secondaryDerivedMetricRawData
}: {
  primaryRawData: [number, number][];
  secondaryRawData: [number, number][];
  primaryDerivedMetricRawData: {
    dataSource1: [number, number][];
    dataSource2: [number, number][];
  };
  secondaryDerivedMetricRawData: {
    dataSource1: [number, number][];
    dataSource2: [number, number][];
  };
}) => {
  const { startWeek, endWeek, id: periodId } = useMainTimePeriod();
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const comparisonPeriod = useComparisonPeriod();

  const adjustedStartWeek = periodId === '1w' ? endWeek - 1 : startWeek;
  const adjustedEndWeek = endWeek;

  // For 1-week periods, comparison should use only the latest week (endWeek) instead of the range
  const adjustedComparisonStartWeek = periodId === '1w' ? comparisonEndWeek : comparisonStartWeek;
  const adjustedComparisonEndWeek = periodId === '1w' ? comparisonEndWeek : comparisonEndWeek;

  const primarySeries = [
    ...(comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && secondaryRawData.length > 0
      ? [
          [
            weekIdToTimestamp(secondaryRawData[secondaryRawData.length - 1][0]),
            secondaryRawData[secondaryRawData.length - 1][1]
          ]
        ]
      : []),
    ...primaryRawData.map(([weekId, value]) => [weekIdToTimestamp(weekId), value])
  ];

  const secondarySeries =
    comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
      ? secondaryRawData.map(([weekId, value]) => [weekIdToTimestamp(getNextYearWeekId(weekId)), value])
      : secondaryRawData.map(([weekId, value]) => [weekIdToTimestamp(weekId), value]);

  const primaryAverage = getAverage(
    getFilteredRangeData({
      data: primaryRawData,
      startWeek: adjustedStartWeek,
      endWeek: adjustedEndWeek
    })
  );

  const comparisonAverage = getAverage(
    getFilteredRangeData({
      data: secondaryRawData,
      startWeek: adjustedComparisonStartWeek,
      endWeek: adjustedComparisonEndWeek
    })
  );

  // This value is not just average of the one [weekId, value][] values, it sums up all the values in dataSource1 and divides by the sum of all the values in dataSource2
  const primaryDerivedMetricAverage = calculateDerivedMetricAverage({
    numeratorData: primaryDerivedMetricRawData.dataSource1,
    denominatorData: primaryDerivedMetricRawData.dataSource2,
    startWeek: adjustedStartWeek,
    endWeek: adjustedEndWeek
  });

  const comparisonDerivedMetricAverage = calculateDerivedMetricAverage({
    numeratorData: secondaryDerivedMetricRawData.dataSource1,
    denominatorData: secondaryDerivedMetricRawData.dataSource2,
    startWeek: adjustedComparisonStartWeek,
    endWeek: adjustedComparisonEndWeek
  });

  const percentChange = calculatePercentChange(primaryAverage, comparisonAverage);
  const primaryDerivedMetricPercentChange = calculatePercentChange(
    primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage
  );

  const primaryLastWeekValue = primarySeries?.at(-1)?.[1] ?? 0;

  const primaryLastWeekTimestamp = primarySeries?.at(-1)?.[0] ?? 0;

  let comparisonLastWeekValue = 0;

  if (secondarySeries?.length) {
    if (comparisonPeriod === ComparisonPeriod.PRIOR_YEAR) {
      const match = secondarySeries.find(([timestamp]) => timestamp === primaryLastWeekTimestamp);
      comparisonLastWeekValue = match?.[1] ?? 0;
    } else {
      comparisonLastWeekValue = secondarySeries.at(-1)?.[1] ?? 0;
    }
  }

  const primaryPercentChangeLastWeek = calculatePercentChange(primaryLastWeekValue, comparisonLastWeekValue);

  return {
    primarySeries,
    secondarySeries,
    primaryAverage,
    comparisonAverage,
    percentChange,
    primaryDerivedMetricPercentChange,
    primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage,
    primaryLastWeekValue,
    comparisonLastWeekValue,
    primaryPercentChangeLastWeek
  };
};
